// Goal chip inputs and ETA (IMPLEMENTATION §0.2 R16, §2.6 "Goal chip", GDD §11.2; M4a). The goal is the
// latest fired unlock with chips (progress.goal); its optional target prices it:
//   shop        the next copy of that shop item (rules/price priceOf; null when sold out)
//   expansion   the mask's cost (null once it is open)
//   autoIncome  an automated income target in $/s (no cash price, so no ETA)
// ETA = ceil((price − cash) / total income per second over the ETA window (60 s)); 0 when affordable,
// null when nothing is earned. Formatting cases (UX §4 chip: `now!` / `~40s` / `~3m` / `>10m`) use the
// thresholds of ui.json goalChip, passed in by the caller (the sim never reads ui.json):
//   now      affordable (eta 0)
//   seconds  eta < etaSecondsBelow            n = eta seconds
//   minutes  eta ≤ etaMinutesMax minutes      n = eta minutes rounded half up (≥ 1)
//   long     longer, or no income             n = etaMinutesMax
// Best-next-step substitution (R17 v2, lead decision L10, M4c; GDD §11.2): the chip shows the factory
// model's best option (rules/advice.ts bestUpgrade, with its own price and ETA) instead of the scripted goal
//   met  when the goal is already reached — a stale "Build X" chip (M4a #4): a copy of the shop item was
//        bought and is built, the expansion is open, the income target is reached; or
//   far  once the `chip.bottleneck` effect (u17) is on, when the scripted goal is more than
//        `bottleneckEtaSec` away or has no cash ETA (u17, u20).
// When the model has no option (no line yet, nothing priced gains), the old R17 snapshot walk is the
// fallback for the `far` case: a STARVED Gallery with a nameable bottleneck sets `bottleneck` (M4a).
// Ghost (v0.1 playtest fix): a shop goal whose item the player already placed as a ghost (bought, waiting
// for materials) sets `ghost` — the chip asks for the delivery ("Crate needs 2× Lead Chunk · 0/2")
// instead of pricing the next copy, and the shell points Sarge and the hint finger at it.
import { shopItem } from '../bdefs.ts';
import { floorDiv } from '../core/num.ts';
import type { Int } from '../core/types.ts';
import type { Ctx } from '../ctx.ts';
import type { GoalTarget, SimDefs, UnlockDef } from '../defs.ts';
import type { FamId } from '../gen/ids.ts';
import { ghostReqs } from '../ghosts.ts';
import { autoPerSec, etaPerSec } from '../income.ts';
import type { SimState } from '../state.ts';
import { hasEffect } from '../unlocks.ts';
import { bestUpgrade, type UpgradeOption } from './advice.ts';
import { type Bottleneck, bottleneckOf } from './bottleneck.ts';
import { ownedL1, priceOf } from './price.ts';
import { expansionIndex, leafHolds } from './triggers.ts';

/** ui.json goalChip thresholds. */
export interface EtaFormat {
  etaSecondsBelow: Int;
  etaMinutesMax: Int;
  /** The R17 bottleneck replaces a scripted goal more than this many seconds away (GDD §11.2: 2 min). */
  bottleneckEtaSec: Int;
}

export type EtaCase =
  | { k: 'now' }
  | { k: 'seconds'; n: Int }
  | { k: 'minutes'; n: Int }
  | { k: 'long'; n: Int };

/** Seconds until `cash` reaches `price` at `perSec` (ceil); 0 when affordable, null when perSec ≤ 0. */
export function etaSeconds(price: Int, cash: Int, perSec: Int): Int | null {
  if (cash >= price) return 0;
  if (perSec <= 0) return null;
  const left = price - cash;
  return floorDiv(left + perSec - 1, perSec);
}

export function etaCase(sec: Int | null, fmt: EtaFormat): EtaCase {
  const maxSec = fmt.etaMinutesMax * 60;
  if (sec === 0) return { k: 'now' };
  if (sec === null || sec > maxSec) return { k: 'long', n: fmt.etaMinutesMax };
  if (sec < fmt.etaSecondsBelow) return { k: 'seconds', n: sec };
  const m = floorDiv(sec + 30, 60);
  return { k: 'minutes', n: m < 1 ? 1 : m };
}

/** Cash price of a goal target now (null: nothing to pay for — no target, income target, sold out, open). */
export function goalPrice(s: SimState, d: SimDefs, g: GoalTarget | null): Int | null {
  if (g === null) return null;
  switch (g.type) {
    case 'shop':
      return priceOf(s, d, g.id);
    case 'expansion': {
      const i = expansionIndex(d, g.id);
      const e = d.board.expansions[i];
      return e === undefined || i <= s.bounds ? null : e.cost;
    }
    case 'autoIncome':
      return null;
  }
}

export interface GoalView {
  /** Index into defs.unlocks. */
  index: Int;
  unlock: UnlockDef;
  /** copy.json keys in order; the last one is the standing goal ("Now it digs itself!" → "Expand…"). */
  chips: string[];
  /** The Loader checklist (u16): which part families have been made. */
  checklist: { fam: FamId; made: boolean }[];
  target: GoalTarget | null;
  /** Cash price (null = no price / ETA). */
  price: Int | null;
  cash: Int;
  /** Total income $/s over the ETA window (R16). */
  incomePerSec: Int;
  etaSec: Int | null;
  eta: EtaCase | null;
  /** Price reached (the chip turns green and pulses). */
  affordable: boolean;
  /** autoIncome targets: current automated $/s (fixed 120 s window) and the target. */
  income: { current: Int; target: Int } | null;
  /** The goal is already reached (a stale "Build X" chip, M4a #4): the chip shows `advice` instead. */
  met: boolean;
  /** R17 v2 (L10): when set, the chip shows this best next step instead of the scripted goal. */
  advice: GoalAdvice | null;
  /** R17 fallback: set only when the substitution applies but the model has no option (see the header). */
  bottleneck: Bottleneck | null;
  /** A placed ghost of the goal's shop item still waiting for materials (the chip asks for them), else null. */
  ghost: GoalGhost | null;
}

/** A ghost of the goal's shop item: its cell and what it still needs (ghostReqs order). */
export interface GoalGhost {
  bid: Int;
  x: Int;
  y: Int;
  rows: { fam: FamId; tier: Int; n: Int; filled: Int }[];
}

/** The first placed ghost of a shop goal's item (the player bought it; it waits for materials), or null. */
export function goalGhost(s: SimState, d: SimDefs, g: GoalTarget | null): GoalGhost | null {
  if (g === null || g.type !== 'shop') return null;
  const item = shopItem(d, g.id);
  for (const b of s.buildings) {
    const gh = b.ghost;
    if (gh === null) continue;
    const match =
      item.kind === 'deposit' ? b.type === 'deposit' && b.ore === item.ref : b.type === item.ref;
    if (!match) continue;
    return {
      bid: b.id,
      x: b.x,
      y: b.y,
      rows: ghostReqs(d, b.type).map((r, i) => ({
        fam: r.fam,
        tier: r.tier,
        n: r.n,
        filled: gh.filled[i] ?? 0,
      })),
    };
  }
  return null;
}

/** The best next step the chip shows, with its own price and ETA (total income over the ETA window). */
export interface GoalAdvice {
  /** Why the chip substitutes: the goal is met, or it is far away / has no cash ETA (after u17). */
  why: 'met' | 'far';
  option: UpgradeOption;
  price: Int;
  etaSec: Int | null;
  eta: EtaCase;
  affordable: boolean;
}

/**
 * The goal's target is already reached: a shop goal whose item has a bought copy that is built (not a ghost;
 * the free starter copy does not count), an open expansion, an income target at or above its $/s.
 */
export function goalMet(s: SimState, d: SimDefs, g: GoalTarget | null): boolean {
  if (g === null) return false;
  switch (g.type) {
    case 'shop': {
      const item = shopItem(d, g.id);
      if (ownedL1(s, item) <= item.freeCopies) return false;
      for (const b of s.buildings) {
        if (b.ghost !== null) continue;
        const match =
          item.kind === 'deposit'
            ? b.type === 'deposit' && b.ore === item.ref
            : b.type === item.ref;
        if (match) return true;
      }
      return false;
    }
    case 'expansion':
      return expansionIndex(d, g.id) <= s.bounds;
    case 'autoIncome':
      return autoPerSec(s, d) >= g.perSec;
  }
}

/** The R17 bottleneck of the first STARVED Gallery that names one (the fallback), or null. */
function r17Bottleneck(c: Ctx): Bottleneck | null {
  for (const g of c.s.buildings) {
    if (g.type !== 'gallery' || g.ghost !== null) continue;
    const b = bottleneckOf(c, g);
    if (b !== null) return b;
  }
  return null;
}

/**
 * The goal chip view. `best` = a precomputed rules/advice bestUpgrade of this state (callers that already
 * ranked the options pass it; undefined = computed here when the substitution applies).
 */
export function goalView(c: Ctx, fmt: EtaFormat, best?: UpgradeOption | null): GoalView | null {
  const { s, d } = c;
  const u = d.unlocks[s.progress.goal];
  if (u === undefined) return null;
  const price = goalPrice(s, d, u.goal);
  const incomePerSec = etaPerSec(s, d);
  const etaSec = price === null ? null : etaSeconds(price, s.wallet.cash, incomePerSec);
  const income =
    u.goal !== null && u.goal.type === 'autoIncome'
      ? { current: autoPerSec(s, d), target: u.goal.perSec }
      : null;
  const met = goalMet(s, d, u.goal);
  const ghost = met ? null : goalGhost(s, d, u.goal);
  const bn = hasEffect(s, d, 'chip.bottleneck');
  const far = bn && (etaSec === null || etaSec > fmt.bottleneckEtaSec);
  let advice: GoalAdvice | null = null;
  let bottleneck: Bottleneck | null = null;
  if (ghost === null && (met || far)) {
    const opt = best === undefined ? bestUpgrade(c) : best;
    if (opt !== null) {
      const eta = etaSeconds(opt.price, s.wallet.cash, incomePerSec);
      advice = {
        why: met ? 'met' : 'far',
        option: opt,
        price: opt.price,
        etaSec: eta,
        eta: etaCase(eta, fmt),
        affordable: s.wallet.cash >= opt.price,
      };
    } else if (far) {
      bottleneck = r17Bottleneck(c);
    }
  }
  return {
    index: s.progress.goal,
    unlock: u,
    chips: u.chips.slice(),
    checklist: u.checklist.map((fam) => ({
      fam,
      made: leafHolds(s, d, { type: 'made', fam, minTier: 1 }),
    })),
    target: u.goal,
    price,
    cash: s.wallet.cash,
    incomePerSec,
    etaSec,
    eta: price === null ? null : etaCase(etaSec, fmt),
    affordable: ghost === null && price !== null && s.wallet.cash >= price,
    income,
    met,
    advice,
    bottleneck,
    ghost,
  };
}
